import { FormikProps } from "formik";
import {
  FGCheckboxInput,
  FGCustomPanel,
  FGListen,
  FGTextAreaInput,
  FGTextInput,
  FieldGroup,
  FieldSet,
} from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";
import styled from "styled-components";
import * as Yup from "yup";

import { CommissionRequestFilesList } from ".";
import { DysplasiaApi, DysplasiaDto, ECommissionType, ERequestState, EUserProfile } from "../../api";
import {
  FGSRSHCheckboxInput,
  FGSRSHDateMaskInput,
  FGSRSHMultiCheckboxInput,
  SmallFormGenerator,
} from "../../components";
import { useAuth, useDialog, useGlobalData } from "../../contexts";
import { ERoutes } from "../../ERoutes";
import { useApiService, useCrudApi, useTl } from "../../hooks";
import { useReferential } from "../../hooks/useReferential";
import { ETLCodes } from "../../locales";
import { DysplasiaEvaluation } from "./DysplasiaEvaluation";
import { VeterinaryGrid } from "./VeterinaryGrid";

const StyledEvaluation = styled.div`
  margin-left: 30px;
`;
export interface IEvaluationDetailPageProps {
  route: ERoutes;
  isAppeal?: boolean;
  comissionType?: ECommissionType;
}

export const EvaluationDetailPage: React.FunctionComponent<IEvaluationDetailPageProps> = ({
  route,
  isAppeal = false,
  comissionType = null,
}) => {
  const api = useApiService(DysplasiaApi);
  const { userPermissions, user } = useAuth();
  const { id, state } = useParams<{ id: string; state: string }>();
  const { t } = useTl();
  const { currentLanguage } = useGlobalData();
  const history = useHistory();
  const { showDialogPromise } = useDialog();

  /* ========== Referentials ===========*/
  const [dysplasiaTypes, dtloading] = useReferential((a) => a.getDysplasiaTypes(true, 0), true);

  const [definitiveSave, setDefinitiveSave] = React.useState(false);

  const comissionRightString = React.useMemo(
    () =>
      comissionType === ECommissionType.GentCommission
        ? "GENTCOMMISSION"
        : comissionType === ECommissionType.ClassicCommission
        ? "COMMISSIONDYSPLASIA"
        : "APPEALDYSPLASIA",
    [comissionType]
  );

  /* ========== Getting Data ===========*/
  const { data, loading, saveItem, saving, deleting, deleteItem, refresh } = useCrudApi<DysplasiaDto>(
    React.useMemo(
      () => ({
        getApiFn: () => api.get(+id, currentLanguage.languageId),
        saveApiFn: (d: DysplasiaDto) => api.saveDysplasiaEvaluation(d),
        onSavedRoute: (d: DysplasiaDto) => (definitiveSave ? `${route}` : `${route}/${+id}/edit`),
        deleteApiFn: (d: DysplasiaDto) => {},
      }),
      [api, currentLanguage.languageId, definitiveSave, id, route]
    ),
    [id, currentLanguage.languageId]
  );

  const saveEvaluations = React.useCallback(
    async (d: DysplasiaDto) => {
      if (definitiveSave) {
        var dialogResult = await showDialogPromise({
          message: t(ETLCodes.DefinitiveSaveWarning),
          title: t(ETLCodes.DefinitiveSave),
        });
        if (dialogResult !== "yes") return;
        saveItem(d);
      } else {
        saveItem(d);
      }
      refresh();
    },
    [definitiveSave, refresh, saveItem, showDialogPromise, t]
  );

  /* ========== Yup ===========*/
  const FormSchema = React.useMemo(() => {
    if (definitiveSave) {
      return Yup.object().shape({
        transientDysplasias: Yup.array().of(
          Yup.object().shape({
            characteristicValueId: Yup.number()
              .nullable()
              .test("required", t(ETLCodes.Required), async function (value) {
                if (this.parent.isCharacteristicValue && !this.parent.isRefused) {
                  return !!value;
                }
                return true;
              }),
            numericValue: Yup.number()
              .nullable()
              .test("required", t(ETLCodes.Required), async function (value) {
                if (!this.parent.isCharacteristicValue && !this.parent.isRefused) {
                  return !!value;
                }
                return true;
              }),
          })
        ),
        transientDysplasiaTypes: Yup.array().of(
          Yup.object().shape({
            officialRemark: Yup.string()
              .nullable()
              .when("isRefused", {
                is: true,
                then: Yup.string().nullable().required(t(ETLCodes.Required)),
                otherwise: Yup.string().nullable(),
              }),
          })
        ),
      });
    }
    return Yup.object().shape({
      transientDysplasiaTypes: Yup.array().of(
        Yup.object().shape({
          officialRemark: Yup.string()
            .nullable()
            .when("isRefused", {
              is: true,
              then: Yup.string().nullable().required(t(ETLCodes.Required)),
              otherwise: Yup.string().nullable(),
            }),
        })
      ),
    });
  }, [definitiveSave, t]);

  const onCancel = React.useCallback(() => history.push(`${route}`), [history, route]);

  const checkCanUseDefinitiveSave = React.useCallback((formik: FormikProps<DysplasiaDto>) => {
    var canSave = formik?.values?.transientDysplasiaTypes.every(
      (tdt) =>
        (tdt.isRefused && !!tdt.officialRemark) ||
        (!tdt.isRefused &&
          !!tdt.transientDysplasias.every((td) => !!td.alphaValue || !!td.numericValue || !!td.healthValueId))
    );

    if (!canSave && formik.values.definitiveSave) {
      formik.setFieldValue("definitiveSave", false);
    }
    return canSave;
  }, []);

  return (
    <SmallFormGenerator
      initialValues={data}
      editMode={
        state === "edit" && user.profile !== EUserProfile.God && data?.request?.status === ERequestState.TO_EVALUATE
      }
      onSubmit={saveEvaluations}
      validationSchema={FormSchema}
      loading={loading}
      saving={saving}
      deleting={deleting}
      onDelete={deleteItem}
      onCancel={onCancel}
      hideButtons={user.profile === EUserProfile.God || data?.request.status !== ERequestState.TO_EVALUATE}
    >
      <FGListen field="definitiveSave" onChanged={(value) => setDefinitiveSave(value)} />
      <FieldSet title={t(ETLCodes.Request)}>
        <FieldGroup columns={2}>
          <FieldGroup>
            <FieldGroup>
              <FGSRSHDateMaskInput name="request.requestDate" label={t(ETLCodes.RequestDate)} readonly />
            </FieldGroup>
            <FieldGroup>
              <FGTextInput name="request.requestNumber" label={t(ETLCodes.RequestNumber)} readonly />
            </FieldGroup>
            <FieldGroup>
              <FGTextInput name="positionTranslated" label={t(ETLCodes.Position)} readonly />
            </FieldGroup>
            <FieldGroup>
              <FGSRSHDateMaskInput name="birthDate" label={t(ETLCodes.BirthDate)} readonly />
            </FieldGroup>
            <FieldGroup>
              <FGTextInput name="breed" label={t(ETLCodes.Breed)} readonly />
            </FieldGroup>
          </FieldGroup>
          <FieldGroup>
            <FGSRSHMultiCheckboxInput
              name="dysplasiaTypesChecked"
              items={dysplasiaTypes}
              loading={dtloading}
              columns={2}
              readonly
            />
          </FieldGroup>
        </FieldGroup>
      </FieldSet>
      <FieldSet title={t(ETLCodes.Remark)}>
        <FGTextAreaInput name="remark" label={t(ETLCodes.Remark)} readonly />
      </FieldSet>
      <FieldSet title={t(ETLCodes.Documents)}>
        <FGCustomPanel>
          {(ctx) => (
            <CommissionRequestFilesList sharepointUri={ctx.formik.values?.sharepointUri}></CommissionRequestFilesList>
          )}
        </FGCustomPanel>
      </FieldSet>
      <FieldSet title={t(ETLCodes.Evaluations)}>
        {data?.transientDysplasiaTypes.map((v, index) => (
          <StyledEvaluation key={index}>
            <FieldSet title={`${t(ETLCodes.DysplasiaType)}: ${v.dysplasiaType}`}>
              {userPermissions.some(
                (c) =>
                  c.module.toUpperCase() === comissionRightString &&
                  c.tab?.toUpperCase() === "VETERINARYGRID" &&
                  c.permission.includes("R")
              ) && (
                <FGCustomPanel>
                  {(ctx) =>
                    !isAppeal &&
                    comissionType === ECommissionType.ClassicCommission &&
                    v.allVetNotesFinished && (
                      <div style={{ marginLeft: "3rem", marginBottom: "1rem" }}>
                        <VeterinaryGrid
                          dysplasiaRequestId={ctx.formik.values.dysplasiaRequestId}
                          dysplasiaTypeId={v.dysplasiaTypeId}
                        />
                      </div>
                    )
                  }
                </FGCustomPanel>
              )}
              <FGSRSHCheckboxInput name={`transientDysplasiaTypes[${index}].isRefused`} label={t(ETLCodes.IsRefused)} />
              <FGTextAreaInput
                name={`transientDysplasiaTypes[${index}].officialRemark`}
                label={t(ETLCodes.OfficialRemark)}
              />
              {v?.transientDysplasias.map((e, eIndex) => (
                <FGCustomPanel key={eIndex}>
                  {(ctx) => (
                    <StyledEvaluation>
                      <DysplasiaEvaluation
                        data={e}
                        index={eIndex}
                        upperIndex={index}
                        isRefused={ctx.formik.values.transientDysplasiaTypes[index].isRefused}
                      />
                    </StyledEvaluation>
                  )}
                </FGCustomPanel>
              ))}
            </FieldSet>
          </StyledEvaluation>
        ))}
        <FieldGroup>
          {data?.request.status === ERequestState.TO_EVALUATE && !saving ? (
            <div style={{ float: "right", marginRight: "20px" }}>
              <FGCustomPanel>
                {(ctx) => (
                  <FGCheckboxInput
                    name="definitiveSave"
                    label={t(ETLCodes.DefinitiveSave)}
                    disabled={!checkCanUseDefinitiveSave(ctx.formik) || saving}
                  />
                )}
              </FGCustomPanel>
            </div>
          ) : null}
        </FieldGroup>
      </FieldSet>
    </SmallFormGenerator>
  );
};
